import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Droplets, Layers, Paintbrush } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const RESOURCES = [
    {
        slug: "pathologies-humidite",
        icon: Droplets,
        eyebrow: "Pathologies",
        title: "Humidité, salpêtre, condensation",
        excerpt: "90 % des désordres d'un mur ancien viennent d'un problème d'eau. Comprendre l'origine avant de traiter.",
    },
    {
        slug: "enduits-chaux-platre-argile",
        icon: Layers,
        eyebrow: "Enduits",
        title: "Chaux, plâtre & argile : quel choix ?",
        excerpt: "Le bon liant compte plus que le bon artisan. Guide de choix par support et exposition.",
    },
    {
        slug: "peintures-decoratives-bati-ancien",
        icon: Paintbrush,
        eyebrow: "Décoration",
        title: "Peintures & finitions respirantes",
        excerpt: "Badigeons, laits de chaux, silicate, stuc, tadelakt : donner une âme à un mur ancien.",
    },
];

export default function Expertise() {
    const ref = useReveal();
    return (
        <section
            id="expertise"
            ref={ref}
            data-testid="expertise-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="label-overline text-brand">Expertise & ressources</span>
                        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                            Comprendre <span className="italic">avant</span> d'agir.
                        </h2>
                        <p className="mt-5 text-stone2 leading-relaxed max-w-xl">
                            Des articles techniques rédigés à partir du terrain, pour vous aider à
                            décider en connaissance de cause avant tout diagnostic ou chantier.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone2">
                        <BookOpen size={16} className="text-brand" />
                        <span>Nouvelles publications chaque mois</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {RESOURCES.map((r, idx) => {
                        const Icon = r.icon;
                        return (
                            <Link
                                to={`/${r.slug}`}
                                key={r.slug}
                                data-testid={`resource-${r.slug}`}
                                className="reveal hover-lift group bg-card border border-border rounded-sm p-7 md:p-8 flex flex-col"
                                style={{ transitionDelay: `${idx * 90}ms` }}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div className="w-11 h-11 rounded-sm bg-sand/70 flex items-center justify-center text-brand">
                                        <Icon size={20} strokeWidth={1.6} />
                                    </div>
                                    <ArrowUpRight
                                        size={18}
                                        className="text-stone2 opacity-50 group-hover:opacity-100 group-hover:text-brand transition"
                                    />
                                </div>
                                <span className="label-overline text-brand mb-2">{r.eyebrow}</span>
                                <h3 className="font-heading text-xl font-medium tracking-tight text-ink leading-snug">
                                    {r.title}
                                </h3>
                                <p className="mt-3 text-sm text-stone2 leading-relaxed">
                                    {r.excerpt}
                                </p>
                                <span className="mt-6 text-sm font-medium text-brand inline-flex items-center gap-1">
                                    Lire l'article
                                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
